import React, { useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { purchaseFlow } from '../../help/flows';
import { SUPPLIER_BILL_STAGES } from '../../help/statusStages';
import FcAmountLine from '../common/FcAmountLine';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Link, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { SupplierBillService } from '../../services/supplierBill.service';
import {
  SupplierBill, SupplierBillLine, SupplierBillStatus, SUPPLIER_BILL_STATUS_COLORS,
  SupplierBillEdit, isOnApprovalHold, approvalHoldLabel, APPROVAL_HOLD_TIP,
} from '../../interfaces/procurement/supplierBill.interface';
import { ReverseConfirmDialog } from './ReverseConfirmDialog';
import ApprovalStatusCard from '../approvals/ApprovalStatusCard';   // FW-3B
import RegisterFromBillLineDialog from '../fixedAsset/RegisterFromBillLineDialog'; // B4-E
import { FixedAssetValueService } from '../../services/fixedAssetValue.service';
import { allBillLinesRegistered } from '../fixedAsset/fixedAssetFormRules';
import { useToast } from '../../context/ToastContext';
import AttachmentsPanel from '../common/AttachmentsPanel';
import { usePermission } from '../../hooks/usePermission';
import { helpWhy } from '../../help/why';
import { PERMISSIONS } from '../../constants/permissions';
import { EditHistoryPanel, EditHistoryRow } from './EditHistoryPanel';
import ScrollableTable from '../common/ScrollableTable';
import { JournalEntryService } from '../../services/journal-entry.service';
import { JournalEntryLine } from '../../interfaces/accounting.interface';
import { DebitNoteService } from '../../services/debitNote.service';
import { DebitNote } from '../../interfaces/procurement/debitNote.interface';
import { SupplierPaymentService } from '../../services/supplierPayment.service';
import { SupplierPayment } from '../../interfaces/procurement/supplierPayment.interface';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { formatFcPrice, isForeignCurrency } from '../../utils/fcDocuments'; // G5a
import { taxCodeLabel, useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import { DocumentTaxCell, useDocumentTaxBreakdown } from '../tax/DocumentTaxBreakdown';

const STATUS_COLORS = SUPPLIER_BILL_STATUS_COLORS;

const REVERSIBLE_STATUSES: SupplierBillStatus[] = ['UNPAID', 'PARTIALLY_PAID', 'PAID', 'BLOCKED'];
const PAYMENT_ELIGIBLE_STATUSES: SupplierBillStatus[] = ['UNPAID', 'PARTIALLY_PAID'];

export const SupplierBillDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [bill, setBill] = useState<SupplierBill | null>(null);
  const anyCodedLine = !!bill?.lines?.some(l => l.taxCodeId);
  const taxLookup = useTaxCodeLookup(anyCodedLine ? bill?.institutionId : null);
  const taxComponents = useDocumentTaxBreakdown('supplier-bill', bill?.id, anyCodedLine);
  const [loading, setLoading] = useState(true);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [justification, setJustification] = useState('');
  const [payments, setPayments] = useState<SupplierPayment[]>([]);
  // W3-E: debit notes against this bill (best effort).
  const [debitNotes, setDebitNotes] = useState<DebitNote[]>([]);
  // W3-F: a service bill (no PO) stores no line rows; its account-coded lines are read from its journal entry.
  const [jeLines, setJeLines] = useState<JournalEntryLine[]>([]);
  const [editHistoryRows, setEditHistoryRows] = useState<EditHistoryRow[]>([]);
  const canManageBill = usePermission(PERMISSIONS.SUPPLIER_BILL_MANAGE);
  // B4-E: registering an asset needs the asset key, journal posting, and reading the bill's journal
  const canAssetManage = usePermission(PERMISSIONS.FIXED_ASSET_MANAGE);
  const canJournalPost = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST);
  const canBillView = usePermission(PERMISSIONS.SUPPLIER_BILL_VIEW);
  const canMakeRecurring = usePermission(PERMISSIONS.SUPPLIER_BILL_CREATE); // FW-2C
  const canPay = usePermission(PERMISSIONS.SUPPLIER_PAYMENT_MANAGE); // server: payment create = SUPPLIER_PAYMENT_MANAGE
  const canManageAssets = canAssetManage && canJournalPost && canBillView;
  const [registerAssetOpen, setRegisterAssetOpen] = useState(false); // B4-E
  // Polish 18: hide "Register as fixed asset" once every candidate line is already an asset
  const [allLinesRegistered, setAllLinesRegistered] = useState(false);
  const loadRegisteredState = (billId: string) => {
    Promise.resolve(FixedAssetValueService.billLines(billId))
      .then(ls => setAllLinesRegistered(allBillLinesRegistered(ls)))
      .catch(() => setAllLinesRegistered(false));
  };

  useEffect(() => {
    if (!id) return;
    SupplierBillService.getById(id)
      .then(b => {
        setBill(b);
        if (!b.purchaseOrderId && !b.openingBalance && b.journalEntryId) {
          Promise.resolve(JournalEntryService.getEntryById(b.journalEntryId))
            .then(je => setJeLines((je?.lines ?? []).filter((l: JournalEntryLine) => l.entryType === 'DEBIT')))
            .catch(() => setJeLines([]));
        }
        if (canManageAssets && !b.openingBalance) loadRegisteredState(b.id);
        SupplierPaymentService.listByBill(b.id, 0, 50)
          .then(r => setPayments(r.content ?? []))
          .catch(() => setPayments([]));
        Promise.resolve(DebitNoteService.listByBill(b.id))
          .then(n => setDebitNotes(Array.isArray(n) ? n : []))
          .catch(() => setDebitNotes([]));
        if ((b.editCount ?? 0) > 0) {
          SupplierBillService.editHistory(id)
            .then((edits: SupplierBillEdit[]) => {
              setEditHistoryRows(edits.map(e => ({
                editSeq: e.editSeq,
                editedAt: e.editedAt,
                editReason: e.editReason,
                summary: `total ${formatMoney(e.prevTotalAmount)} → ${formatMoney(b.totalAmount)} (was ${humanizeEnum(e.prevStatus)})`,
              })));
            })
            .catch(() => setEditHistoryRows([]));
        }
      })
      .catch(e => toast.showApiError(e, 'Failed to load supplier bill'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReverse = async () => {
    if (!bill) return;
    setReversing(true);
    try {
      const updated = await SupplierBillService.reverse(bill.id);
      setBill(updated);
      toast.showSuccess('Supplier bill reversed');
      setReverseDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse bill');
    } finally {
      setReversing(false);
    }
  };

  const handleRelease = async () => {
    if (!bill) return;
    setReleasing(true);
    try {
      const updated = await SupplierBillService.releaseBlock(bill.id, justification.trim());
      setBill(updated);
      toast.showSuccess('Bill released for payment');
      setReleaseDialogOpen(false);
      setJustification('');
    } catch (e) {
      toast.showApiError(e, 'Failed to release bill');
    } finally {
      setReleasing(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!bill) return <Box sx={{ p: 3 }}><Typography>Supplier bill not found.</Typography></Box>;

  const showReverse = canManageBill && REVERSIBLE_STATUSES.includes(bill.status);   // server: reverse = SUPPLIER_BILL_MANAGE
  const canReverse = bill.status === 'UNPAID' || bill.status === 'BLOCKED';   // server refuses a bill that has payments
  const canRecordPayment = canPay && PAYMENT_ELIGIBLE_STATUSES.includes(bill.status);
  const onApprovalHold = isOnApprovalHold(bill);   // FW-3B
  const canRelease = bill.status === 'BLOCKED' && canManageBill && !onApprovalHold;   // FW-3B: a hold by approval is decided in the inbox
  const isServiceBill = !bill.purchaseOrderId && !bill.openingBalance;
  const canEdit = !bill.openingBalance && !isServiceBill && (bill.status === 'UNPAID' || bill.status === 'BLOCKED')
    && (bill.paidAmount ?? 0) === 0
    && (bill.debitedAmount ?? 0) === 0
    && canMakeRecurring;   // server: bill edit = SUPPLIER_BILL_CREATE
  const canDebitNote = canManageBill && !bill.openingBalance && ['UNPAID', 'PARTIALLY_PAID', 'PAID'].includes(bill.status);
  // G5a: a foreign PO bill compares (and lists) its prices in the bill currency
  const fcPoBill = !!bill.purchaseOrderId && isForeignCurrency(bill.currencyCode);
  const poPriceOf = (l: SupplierBillLine) => (fcPoBill ? l.foreignPoUnitPrice : l.poUnitPrice);
  const billPriceOf = (l: SupplierBillLine) => (fcPoBill ? (l.foreignUnitPrice ?? l.unitPrice) : l.unitPrice);
  const priceText = (n: number) => (fcPoBill ? formatFcPrice(bill.currencyCode ?? 'BDT', n) : formatMoney(n));
  const hasVariance = (bill.lines ?? []).some(l => poPriceOf(l) != null && poPriceOf(l) !== billPriceOf(l));

  return (
    <Box sx={{ p: 3 }}>
      <FlowStrip data-help="procurement.bill.flow" stages={purchaseFlow({ current: 'bill', po: { id: bill.purchaseOrderId, no: bill.purchaseOrderNumber }, bill: { no: bill.billNumber } })} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">{bill.billNumber}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <Button data-help="accounting.bill.edit" variant="outlined" onClick={() => navigate(`/procurement/supplier-bills/${bill.id}/edit`)}>
              Edit
            </Button>
          )}
          {canRelease && (
            <Button data-help="accounting.bill.release" variant="contained" color="warning" onClick={() => setReleaseDialogOpen(true)}>
              Release block
            </Button>
          )}
          {showReverse && (
            <Button data-help="accounting.bill.reverse" {...helpWhy(canReverse ? null : { kind: 'status', message: 'Only an Unpaid or Blocked bill can be reversed. Reverse the payments first.' })} disabled={!canReverse} variant="outlined" color="error" onClick={() => setReverseDialogOpen(true)}>
              Reverse
            </Button>
          )}
          {canManageAssets && !allLinesRegistered && !bill.openingBalance && ['UNPAID', 'PARTIALLY_PAID', 'PAID'].includes(bill.status) && (
            <Button data-help="procurement.billDetail.registerAsset" variant="outlined" onClick={() => setRegisterAssetOpen(true)}>Register as fixed asset</Button>
          )}
          {canDebitNote && (
            <Button data-help="accounting.bill.debitNote" variant="outlined" onClick={() => navigate(`/procurement/debit-notes/new?supplierBillId=${bill.id}`)}>
              Issue Debit Note
            </Button>
          )}
          {onApprovalHold && (
            <Tooltip title={APPROVAL_HOLD_TIP}>
              <span><Button data-help="procurement.billDetail.payOnHold" variant="contained" disabled data-testid="pay-on-hold">Record Payment</Button></span>
            </Tooltip>
          )}
          {canRecordPayment && (
            <Button
              data-help="accounting.bill.recordPayment"
              variant="contained"
              color="primary"
              onClick={() => navigate(`/procurement/supplier-payments/new?supplierBillId=${bill.id}`)}
            >
              Record Payment
            </Button>
          )}
          {/* ---- FW-2C ---- */}
          {canMakeRecurring && (
            <Button data-help="procurement.billDetail.makeRecurring" variant="outlined" onClick={() => navigate(`/procurement/recurring-bills?prefillKind=SUPPLIER_BILL&prefillDoc=${bill.id}`)}>Make recurring</Button>
          )}
          {/* ---- end FW-2C ---- */}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Bill Number</Typography>
            <Typography>{bill.billNumber}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            {onApprovalHold ? (
              <Tooltip title={APPROVAL_HOLD_TIP}>
                <Chip label={approvalHoldLabel(bill)} color="warning" size="small" data-testid="approval-hold-chip" />
              </Tooltip>
            ) : (
              <StatusStagesChip data-help="procurement.bill.statusStages" label={humanizeEnum(bill.status)} status={bill.status} stages={SUPPLIER_BILL_STAGES} color={STATUS_COLORS[bill.status]} />
            )}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Purchase Order</Typography>
            <Typography>{bill.purchaseOrderNumber ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Supplier</Typography>
            <Typography>{bill.supplierName ?? '—'}</Typography>
          </Box>
          {bill.supplierInvoiceRef && (
            <Box>
              <Typography variant="body2" color="text.secondary">Supplier Invoice Ref</Typography>
              <Typography>{bill.supplierInvoiceRef}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">Bill Date</Typography>
            <Typography>{formatDate(bill.billDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Due Date</Typography>
            <Typography>{formatDate(bill.dueDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography>{formatMoney(bill.subtotal)}</Typography>
          </Box>
          {bill.journalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Journal entry</Typography>
              <Link component={RouterLink} to={`/accounting/journal-entries/${bill.journalEntryId}`}>View journal entry</Link>
            </Box>
          )}
          <DocumentTaxCell total={bill.taxAmount} components={taxComponents} />
          <Box>
            <Typography variant="body2" color="text.secondary">Total Amount</Typography>
            <Typography>{formatMoney(bill.totalAmount)}</Typography>
          </Box>
          <FcAmountLine currency={bill.currencyCode} rate={bill.exchangeRate} foreign={bill.foreignTotal}
            bdt={bill.totalAmount} foreignOutstanding={bill.foreignOutstanding} />
          {fcPoBill && bill.grnFxDifference != null && bill.grnFxDifference !== 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary">FX on receipts (GRN rate to bill rate)</Typography>
              <Typography color={bill.grnFxDifference < 0 ? 'error' : 'success.main'}>
                {bill.grnFxDifference < 0 ? 'Loss' : 'Gain'} {formatMoney(Math.abs(bill.grnFxDifference))}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
            <Typography>{formatMoney(bill.paidAmount)}</Typography>
          </Box>
          {(bill.debitedAmount ?? 0) > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary">Debited</Typography>
              <Typography>{formatMoney(bill.debitedAmount)}</Typography>
            </Box>
          )}
          {bill.status === 'REVERSED' && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">Reversed Date</Typography>
                <Typography>{bill.reversedDate ? formatDate(bill.reversedDate) : '-'}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {/* ---- FW-3B: the approval of the bill (who decided, who is still to decide) ---- */}
      {(bill.approvalStatus ?? 'NONE') !== 'NONE' && (
        <ApprovalStatusCard subjectType="SUPPLIER_BILL" subjectId={bill.id} refreshKey={`${bill.approvalStatus}`} />
      )}
      {/* ---- end FW-3B ---- */}

      {(bill.blockReason || hasVariance) && (
        <Paper sx={{ p: 3, mb: 3 }} data-help-section="procurement.billDetail.variance">
          <Typography variant="h6" sx={{ mb: 1 }}>Variance Summary</Typography>
          {bill.blockReason && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>{bill.blockReason}</Typography>
          )}
          <ScrollableTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="procurement.billDetail.var.item">Item</TableCell>
                    <TableCell data-help="procurement.billDetail.var.po">PO Price</TableCell>
                    <TableCell data-help="procurement.billDetail.var.bill">Bill Price</TableCell>
                    <TableCell data-help="procurement.billDetail.var.delta">Δ</TableCell>
                    <TableCell data-help="procurement.billDetail.var.pct">Δ%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(bill.lines ?? []).map(line => {
                    const po = poPriceOf(line);
                    const billPrice = billPriceOf(line);
                    const delta = po != null ? Math.round((billPrice - po) * 10000) / 10000 : null;
                    const deltaPct = po != null && po !== 0 ? (delta! / po) * 100 : null;
                    const off = delta != null && delta !== 0;
                    return (
                      <TableRow key={line.id} sx={off ? { bgcolor: 'error.light' } : undefined}>
                        <TableCell>{line.itemName ?? '—'}{line.itemCode ? ` (${line.itemCode})` : ''}</TableCell>
                        <TableCell>{po != null ? priceText(po) : '-'}</TableCell>
                        <TableCell>{priceText(billPrice)}</TableCell>
                        <TableCell>{delta != null ? priceText(delta) : '-'}</TableCell>
                        <TableCell>{deltaPct != null ? `${deltaPct.toFixed(2)}%` : '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
          </ScrollableTable>
        </Paper>
      )}

      {(bill.lines ?? []).length > 0 && (
        <Paper sx={{ p: 3 }} data-help-section="procurement.billDetail.lines">
          <Typography variant="h6" sx={{ mb: 2 }}>Lines</Typography>
          <ScrollableTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="procurement.billDetail.col.item">Item</TableCell>
                    <TableCell data-help="procurement.billDetail.col.qty">Quantity</TableCell>
                    {fcPoBill && <TableCell data-help="procurement.billDetail.col.fcPrice">Unit Price ({bill.currencyCode})</TableCell>}
                    <TableCell data-help="procurement.billDetail.col.price">Unit Price{fcPoBill ? ' (BDT)' : ''}</TableCell>
                    {anyCodedLine && <TableCell data-help="procurement.billDetail.col.taxCode">Tax code</TableCell>}
                    {anyCodedLine && <TableCell data-help="procurement.billDetail.col.tax" align="right">Line Tax</TableCell>}
                    <TableCell data-help="procurement.billDetail.col.total">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(bill.lines ?? []).map(line => (
                    <TableRow key={line.id}>
                      <TableCell>{line.itemName ?? '—'}{line.itemCode ? ` (${line.itemCode})` : ''}</TableCell>
                      <TableCell>{line.quantity}</TableCell>
                      {fcPoBill && <TableCell>{line.foreignUnitPrice != null ? formatFcPrice(bill.currencyCode ?? 'BDT', line.foreignUnitPrice) : '—'}</TableCell>}
                      <TableCell>{formatMoney(line.unitPrice)}</TableCell>
                      {anyCodedLine && <TableCell>{taxCodeLabel(taxLookup, line.taxCodeId)}</TableCell>}
                      {anyCodedLine && <TableCell align="right">{line.lineTax != null ? formatMoney(line.lineTax) : '—'}</TableCell>}
                      <TableCell>{formatMoney(line.lineTotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </ScrollableTable>
        </Paper>
      )}

      {isServiceBill && jeLines.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }} data-help-section="procurement.billDetail.accounts">
          <Typography variant="h6" sx={{ mb: 2 }}>Account lines</Typography>
          <ScrollableTable>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell data-help="procurement.billDetail.acc.account">Account</TableCell><TableCell data-help="procurement.billDetail.acc.desc">Description</TableCell><TableCell data-help="procurement.billDetail.acc.amount" align="right">Amount</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {jeLines.map((l, i) => (
                  <TableRow key={l.id ?? i}>
                    <TableCell>{l.accountCode ? `${l.accountCode} - ` : ''}{l.accountName ?? l.accountId}</TableCell>
                    <TableCell>{l.description ?? '—'}</TableCell>
                    <TableCell align="right">{formatMoney(l.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        </Paper>
      )}

      <Paper sx={{ p: 3, mt: 3 }} data-help-section="procurement.billDetail.payments">
        <Typography variant="h6" sx={{ mb: 2 }}>Payments</Typography>
        {payments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No payments recorded against this bill yet.</Typography>
        ) : (
          <ScrollableTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="procurement.billDetail.pay.number">Payment</TableCell>
                  <TableCell data-help="procurement.billDetail.pay.date">Date</TableCell>
                  <TableCell data-help="procurement.billDetail.pay.amount">Amount</TableCell>
                  <TableCell data-help="procurement.billDetail.pay.status">Status</TableCell>
                  <TableCell data-help="procurement.billDetail.pay.from">Paid From</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map(pay => (
                  <TableRow
                    key={pay.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/procurement/supplier-payments/${pay.id}`)}
                  >
                    <TableCell>{pay.paymentNumber}</TableCell>
                    <TableCell>{formatDate(pay.paymentDate)}</TableCell>
                    <TableCell>{formatMoney(pay.amount)}</TableCell>
                    <TableCell>{humanizeEnum(pay.status)}</TableCell>
                    <TableCell>{pay.paymentAccountName ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        )}
      </Paper>

      {debitNotes.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }} data-help-section="procurement.billDetail.debitNotes">
          <Typography variant="h6" sx={{ mb: 2 }}>Debit Notes</Typography>
          <ScrollableTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="procurement.billDetail.dn.number">Debit note</TableCell>
                  <TableCell data-help="procurement.billDetail.dn.date">Date</TableCell>
                  <TableCell data-help="procurement.billDetail.dn.amount">Amount</TableCell>
                  <TableCell data-help="procurement.billDetail.dn.status">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {debitNotes.map(n => (
                  <TableRow key={n.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/procurement/debit-notes/${n.id}`)}>
                    <TableCell>{n.debitNoteNumber}</TableCell>
                    <TableCell>{formatDate(n.noteDate)}</TableCell>
                    <TableCell>{formatMoney(n.totalAmount)}</TableCell>
                    <TableCell>{humanizeEnum(n.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        </Paper>
      )}

      {(bill.editCount ?? 0) > 0 && (
        <EditHistoryPanel title="Edit history" rows={editHistoryRows} />
      )}

      {registerAssetOpen && (
        <RegisterFromBillLineDialog open institutionId={bill.institutionId} billId={bill.id}
          onClose={() => { setRegisterAssetOpen(false); loadRegisteredState(bill.id); }} />
      )}
      <ReverseConfirmDialog
        open={reverseDialogOpen}
        entityLabel="Supplier Bill"
        consequence="AP and Inventory accruals will be reversed. Any payments must be reversed first."
        onCancel={() => setReverseDialogOpen(false)}
        onConfirm={handleReverse}
        loading={reversing}
      />

      <Dialog open={releaseDialogOpen} onClose={() => setReleaseDialogOpen(false)} fullWidth maxWidth="sm" data-help-page="procurement.billReleaseForm.page">
        <DialogTitle>Release blocked bill</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This bill is blocked because its price or tax differs from the purchase order. Releasing it makes it payable;
            the posted journal entry is unchanged. A justification is required.
          </DialogContentText>
          <TextField
            data-help="procurement.billReleaseForm.field.justification"
            label="Justification"
            value={justification}
            onChange={e => setJustification(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.billReleaseForm.cancel" onClick={() => setReleaseDialogOpen(false)} disabled={releasing}>Cancel</Button>
          <Button
            data-help="procurement.billReleaseForm.save"
            variant="contained"
            color="warning"
            onClick={handleRelease}
            disabled={releasing || justification.trim().length === 0}
          >
            {releasing ? <CircularProgress size={20} /> : 'Confirm release'}
          </Button>
        </DialogActions>
      </Dialog>

      <AttachmentsPanel entityType="SUPPLIER_BILL" entityId={bill.id} canManage={canManageBill} />
    </Box>
  );
};

export default SupplierBillDetail;
